import { useEffect, useState, useCallback } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { useSocket } from "@/hooks/useSocket";
import { setSoundEnabled, sfxNav, sfxLoginSuccess } from "@/utils/soundEngine";
import AppShell from "@/components/layout/AppShell";
import InstitutionSelect from "@/components/auth/InstitutionSelect";
import LoginScreen from "@/components/auth/LoginScreen";
import SolidGroundLogin from "@/components/auth/SolidGroundLogin";
import LaunchAnimation from "@/components/launch/LaunchAnimation";
import FirstLoginModal from "@/components/auth/FirstLoginModal";
// Cookie-Consent lebt jetzt zentral im Core-Gateway (CookieConsent.jsx) —
// jeder Nutzer passiert Core, dort wird DSGVO/DSG-konform eingewilligt.
import NovaBuddy from "@/components/buddy/NovaBuddy";
import RewardOverlay from "@/components/layout/RewardOverlay";

// Student screens
import Dashboard    from "@/components/student/Dashboard";
import Quiz         from "@/components/student/Quiz";
import Arena        from "@/components/student/Arena";
import Leaderboard  from "@/components/student/Leaderboard";
import Shop         from "@/components/student/Shop";
import Vault        from "@/components/student/Vault";
import Achievements from "@/components/student/Achievements";
import Messages     from "@/components/student/Messages";
import Friends      from "@/components/student/Friends";
import Profile      from "@/components/student/Profile";
import Settings       from "@/components/student/Settings";
import Tasks          from "@/components/student/Tasks";
import Badges         from "@/components/student/Badges";
import Titles         from "@/components/student/Titles";
import SeasonalEvents     from "@/components/student/SeasonalEvents";
import Analysis          from "@/components/student/Analysis";
import ClassChallenges   from "@/components/student/ClassChallenges";
import LernCalendar      from "@/components/student/LernCalendar";
import LernWelten        from "@/components/learn/LernWelten";

// Admin-only screens
import AdminDashboard     from "@/components/admin/AdminDashboard";
import AdminReports       from "@/components/admin/AdminReports";
import StudentList        from "@/components/admin/StudentList";
import StudentDetail      from "@/components/admin/StudentDetail";
import CodeManager        from "@/components/admin/CodeManager";
import QuizBuilder        from "@/components/admin/QuizBuilder";
import TaskManager        from "@/components/admin/TaskManager";
import InstitutionManager from "@/components/admin/InstitutionManager";
import GamificationManager from "@/components/admin/GamificationManager";
import MeteorStorm        from "@/components/student/MeteorStorm";

// Teacher-only screen
import TeacherDashboard from "@/components/teacher/TeacherDashboard";

// Mentor + Parent
import MentorDashboard from "@/components/mentor/MentorDashboard";
import ParentDashboard from "@/components/parent/ParentDashboard";

// StudoX Core Hub
import StudoXCore from "@/components/core/StudoXCore";
import CoreDashboard from "@/components/core/CoreDashboard";

// Non-student profile
import NonStudentProfile from "@/components/profile/NonStudentProfile";

/* ── Dynamic platform title + favicon ────────────────────────────────────── */
const PLATFORM_META = {
  core: {
    title: "StudoX Core",
    // Atom orbital favicon: 3 rings + center orb
    favicon: `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'><defs><radialGradient id='o' cx='50%25' cy='40%25' r='60%25'><stop offset='0%25' stop-color='%23f5f3ff'/><stop offset='50%25' stop-color='%23a855f7'/><stop offset='100%25' stop-color='%233b0764'/></radialGradient><linearGradient id='r1' x1='0%25' y1='50%25' x2='100%25' y2='50%25'><stop offset='0%25' stop-color='%23c084fc' stop-opacity='0.1'/><stop offset='50%25' stop-color='%23c084fc' stop-opacity='0.9'/><stop offset='100%25' stop-color='%2306b6d4' stop-opacity='0.1'/></linearGradient></defs><ellipse cx='40' cy='40' rx='36' ry='14' fill='none' stroke='url(%23r1)' stroke-width='2'/><ellipse cx='40' cy='40' rx='36' ry='14' fill='none' stroke='%238b5cf6' stroke-width='1.5' opacity='0.6' transform='rotate(60 40 40)'/><ellipse cx='40' cy='40' rx='36' ry='14' fill='none' stroke='%2306b6d4' stroke-width='1.5' opacity='0.5' transform='rotate(-60 40 40)'/><circle cx='40' cy='40' r='10' fill='url(%23o)'/><circle cx='40' cy='40' r='4' fill='white' opacity='0.9'/></svg>`,
  },
  flow: {
    title: "StudoX Flow",
    favicon: `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0%25' stop-color='%23c4b5fd'/><stop offset='100%25' stop-color='%234c1d95'/></linearGradient></defs><rect width='80' height='80' rx='18' fill='url(%23g)'/><text x='50%25' y='56' text-anchor='middle' font-family='Inter,system-ui,sans-serif' font-weight='800' font-size='44' fill='white' opacity='0.95'>S</text></svg>`,
  },
};

function usePlatformMeta() {
  useEffect(() => {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isCore = host === "core.studox.eu" || host.includes("core") || path === "/core";
    const meta = isCore ? PLATFORM_META.core : PLATFORM_META.flow;

    document.title = meta.title;
    const el = document.getElementById("dynamic-favicon") || document.querySelector("link[rel='icon']");
    if (el) el.href = meta.favicon;
  }, []);
}

/* ── Admin redirect to Core ───────────────────────────────────────────────── */
function AdminCoreRedirect() {
  useEffect(() => {
    import("@/utils/launchCore").then(({ launchCore }) => launchCore());
  }, []);
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      minHeight: "60vh", gap: "1rem", color: "#9ca3af", fontFamily: "Inter, sans-serif",
    }}>
      <div style={{ fontSize: 48 }}>🚀</div>
      <p style={{ fontSize: 14, fontWeight: 700 }}>Das Admin-Dashboard wurde verschoben.</p>
      <p style={{ fontSize: 12 }}>Du wirst zu <strong style={{ color: "#c084fc" }}>core.studox.eu</strong> weitergeleitet…</p>
    </div>
  );
}

/* ── Launch animation persistence key ────────────────────────────────────── */
const LAUNCH_SEEN_KEY = "studox-flow-launch-seen-v1";

function getLaunchSeenFor(userId) {
  try {
    const raw = localStorage.getItem(LAUNCH_SEEN_KEY);
    const data = raw ? JSON.parse(raw) : {};
    return !!data[userId];
  } catch { return false; }
}

function markLaunchSeen(userId) {
  try {
    const raw = localStorage.getItem(LAUNCH_SEEN_KEY);
    const data = raw ? JSON.parse(raw) : {};
    data[userId] = true;
    localStorage.setItem(LAUNCH_SEEN_KEY, JSON.stringify(data));
  } catch {}
}

/* ── Auth flow ────────────────────────────────────────────────────────────── */
// Pre-login states:
// "institution" → user picks school / private
// "login"       → code-based login screen (with optional institution context)
// "admin"       → hidden admin portal

function usePreLoginState() {
  const [preState,     setPreState]     = useState("institution");
  const [institution,  setInstitution]  = useState(null);

  const goLogin    = useCallback((inst) => { setInstitution(inst || null); setPreState("login"); }, []);
  const goPrivate  = useCallback(()      => { setInstitution(null);          setPreState("login"); }, []);
  const goBack     = useCallback(()      => { setPreState("institution"); },  []);

  return { preState, institution, goLogin, goPrivate, goBack };
}

/* ── Route wrapper ────────────────────────────────────────────────────────── */
function PrivateRoute({ children, roles }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user?.role)) return <Navigate to="/" replace />;
  return children;
}

/* ── Route change sound ───────────────────────────────────────────────────── */
function RouteChangeSound() {
  const location = useLocation();
  useEffect(() => { sfxNav(); }, [location.pathname]);
  return null;
}

/* ── SSO Exchange: handle ?sso=<token> from Core / Clubs / other apps ────── */
// Runs once on mount. If a signed SSO token is present in the URL, exchange it
// for a full Flow JWT so the user is auto-logged in without re-entering creds.
function useSSOExchange() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get("sso");
    if (!ssoToken) return;
    // Remove ?sso= from URL immediately — clean URL, prevent re-use on refresh
    window.history.replaceState({}, "", window.location.pathname);
    const backendBase = (import.meta.env.VITE_API_URL || "http://localhost:4000/api").replace(/\/api$/, "");
    fetch(`${backendBase}/api/auth/sso-exchange?token=${encodeURIComponent(ssoToken)}`)
      .then((r) => r.json())
      .then(({ token, user }) => {
        if (token && user) {
          useAuthStore.setState({ token, user, isAuthenticated: true });
        }
      })
      .catch(() => {}); // SSO expired — user sees login screen, can log in manually
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

/* ── Verifying gate screen — shown while the server re-checks a session ─────── */
function VerifyingScreen() {
  return (
    <div style={{ minHeight: "100dvh", background: "#06021a", display: "grid", placeItems: "center", color: "#fff", fontFamily: "Inter, system-ui, sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 48, height: 48, margin: "0 auto 18px", borderRadius: "50%", border: "3px solid rgba(139,92,246,0.25)", borderTopColor: "#8b5cf6", animation: "spin 0.8s linear infinite" }} />
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.08em", color: "#a78bfa" }}>Zugang wird verifiziert…</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 6 }}>StudoX schützt Kinderdaten — einen Moment.</div>
      </div>
    </div>
  );
}

/* ── Bulletproof auth bootstrap helpers ──────────────────────────────────────
   Two guarantees, by construction, because this protects children's data:
   (1) app content NEVER paints without a server-verified session, and
   (2) the app can NEVER loop-bounce to Core (which flashed the screen and
       hammered the backend). */
const CORE_URL   = (import.meta.env.VITE_CORE_URL || "https://core.studox.eu").trim();
const STATUS_URL = "https://status.studox.eu";
const API_BASE   = (import.meta.env.VITE_API_URL || "https://backend-production-dfe8.up.railway.app/api").trim();
const REDIRECT_GUARD_KEY = "studox-auth-redirects-v1";

// Exchange a one-time ?sso= handoff for a full session. Returns true on success.
async function exchangeSSO(ssoToken) {
  try {
    const r = await fetch(`${API_BASE}/auth/sso-exchange?token=${encodeURIComponent(ssoToken)}`);
    if (!r.ok) return false;
    const { token, user } = await r.json();
    if (token && user) { useAuthStore.getState().setSession({ token, user }); return true; }
    return false;
  } catch { return false; }
}

// Redirect circuit-breaker: allow a bounce to Core, but if we keep returning
// unauthenticated (>3× / 30s) STOP and let the caller show an error screen instead
// of looping forever. Returns false once the breaker has tripped.
function bounceToCore() {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(REDIRECT_GUARD_KEY);
    let data = raw ? JSON.parse(raw) : { n: 0, first: now };
    if (now - data.first > 30000) data = { n: 0, first: now };
    data.n += 1;
    sessionStorage.setItem(REDIRECT_GUARD_KEY, JSON.stringify(data));
    if (data.n > 3) return false;
  } catch { /* sessionStorage blocked → still allow the single redirect below */ }
  window.location.replace(`${CORE_URL}/?returnTo=${encodeURIComponent(window.location.href)}`);
  return true;
}
function clearRedirectGuard() { try { sessionStorage.removeItem(REDIRECT_GUARD_KEY); } catch {} }

/* ── Auth error screen ───────────────────────────────────────────────────────
   "backend": the system detected a dead/unreachable service → auto-forward to
   status.studox.eu so the user sees what died.
   "loop": the login kept bouncing → manual retry + status link (no auto-loop). */
function AuthErrorScreen({ kind }) {
  useEffect(() => {
    if (kind === "backend") {
      const t = setTimeout(() => { window.location.href = STATUS_URL; }, 2600);
      return () => clearTimeout(t);
    }
  }, [kind]);
  const backend = kind === "backend";
  const retry = () => { clearRedirectGuard(); window.location.href = `${CORE_URL}/?returnTo=${encodeURIComponent(window.location.href)}`; };
  return (
    <div style={{ minHeight: "100dvh", background: "radial-gradient(1100px 600px at 50% -10%, #15121f, #06050b 60%)", display: "grid", placeItems: "center", color: "#fff", fontFamily: "Inter, system-ui, sans-serif", padding: 24 }}>
      <div style={{ textAlign: "center", maxWidth: 440 }}>
        <img src="/studox-logo.png" alt="StudoX" style={{ height: 64, margin: "0 auto 20px", display: "block", filter: "drop-shadow(0 8px 30px rgba(123,92,255,0.5))" }} />
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 10px" }}>{backend ? "System wird geprüft …" : "Anmeldung fehlgeschlagen"}</h1>
        <p style={{ fontSize: 14.5, lineHeight: 1.6, color: "rgba(255,255,255,0.6)", margin: "0 0 22px" }}>
          {backend
            ? "Ein Dienst antwortet gerade nicht. Wir bringen dich zur Status-Seite, damit du siehst, was los ist."
            : "Der Login konnte nicht abgeschlossen werden. Versuche es erneut — oder sieh auf der Status-Seite nach."}
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={retry} style={{ border: "none", cursor: "pointer", padding: "12px 22px", borderRadius: 12, fontSize: 14.5, fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#7b5cff,#22e3ff)", boxShadow: "0 10px 30px rgba(123,92,255,0.45)" }}>Erneut anmelden</button>
          <a href={STATUS_URL} style={{ textDecoration: "none", padding: "12px 22px", borderRadius: 12, fontSize: 14.5, fontWeight: 700, color: "#fff", border: "1px solid rgba(255,255,255,0.18)" }}>Status ansehen</a>
        </div>
      </div>
    </div>
  );
}

/* ── Main App ─────────────────────────────────────────────────────────────── */
export default function App() {
  const { isAuthenticated, user, isFirstLogin } = useAuthStore();
  const [showLaunch, setShowLaunch] = useState(false);
  // ── ONE bulletproof, sequential auth bootstrap ──────────────────────────────
  // Replaces the old pair of racing effects (SSO-exchange vs token-verify) that
  // could log out a freshly-installed session. Phases: "boot" → "ready" | "error".
  const [authPhase, setAuthPhase] = useState("boot");
  const [authError, setAuthError] = useState(null);
  usePlatformMeta();
  useSocket();

  useEffect(() => {
    let alive = true;
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const search = typeof window !== "undefined" ? window.location.search : "";
    const onCoreHub = host === "core.studox.eu" && (path === "/" || path === "" || path === "/core");
    // Core hub + Solid-Ground portal own their auth — skip the app gate there.
    if (onCoreHub || path === "/core" || search.includes("sg=1")) { setAuthPhase("ready"); return; }

    (async () => {
      const params = new URLSearchParams(search);
      const sso = params.get("sso");
      // 1) one-time SSO handoff from Core — highest priority
      if (sso) {
        window.history.replaceState({}, "", window.location.pathname);
        const ok = await exchangeSSO(sso);
        if (!alive) return;
        if (ok) { clearRedirectGuard(); setAuthPhase("ready"); return; }
      }
      // 2) existing token → verify ONCE against the server
      if (useAuthStore.getState().token) {
        const res = await useAuthStore.getState().verifySession();
        if (!alive) return;
        if (res === "ok")    { clearRedirectGuard(); setAuthPhase("ready"); return; }
        if (res === "error") { setAuthError("backend"); setAuthPhase("error"); return; }
        // res === "invalid" → session already cleared; fall through to bounce
      }
      // 3) not authenticated → bounce to Core (circuit-broken so it can't loop)
      if (!bounceToCore()) { if (alive) { setAuthError("loop"); setAuthPhase("error"); } }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync sound setting from user prefs
  useEffect(() => {
    if (user?.settings?.soundEnabled !== undefined) {
      setSoundEnabled(user.settings.soundEnabled);
    }
  }, [user?.settings?.soundEnabled]);

  // (User refresh + server verification now happen inside the single auth bootstrap
  //  above — verifySession()/exchangeSSO() install user + isAuthenticated atomically.)

  // Show launch animation on first login (once per user)
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      const seen = getLaunchSeenFor(user.id);
      if (!seen) {
        sfxLoginSuccess();
        setTimeout(() => setShowLaunch(true), 100);
      }
    }
  }, [isAuthenticated, user?.id]);

  // Listen for replay request from Settings
  useEffect(() => {
    const handler = () => {
      if (user?.id) {
        // Remove seen flag so it shows again
        try {
          const raw  = localStorage.getItem(LAUNCH_SEEN_KEY);
          const data = raw ? JSON.parse(raw) : {};
          delete data[user.id];
          localStorage.setItem(LAUNCH_SEEN_KEY, JSON.stringify(data));
        } catch {}
        sfxLoginSuccess();
        setShowLaunch(true);
      }
      localStorage.removeItem("studox-replay-launch");
    };
    window.addEventListener("studox-replay-launch", handler);
    return () => window.removeEventListener("studox-replay-launch", handler);
  }, [user?.id]);

  const handleLaunchDone = () => {
    if (user?.id) markLaunchSeen(user.id);
    setShowLaunch(false);
  };

  /* ── System access portals — checked first, before any routing ─────────── */
  if (typeof window !== "undefined" && !isAuthenticated) {
    const search = window.location.search;
    /* Overseer — the single privileged master-key login (Solid Ground). */
    if (search.includes("sg=1")) {
      return <SolidGroundLogin />;
    }
    /* ?sys=1 (shared system-admin master key) was RETIRED — admins are now
       created by the Overseer and log in with Name + PLZ + Code like everyone else. */
  }

  /* ── StudoX Core Hub ────────────────────────────────────────────────────── */
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
  const currentHost = typeof window !== "undefined" ? window.location.hostname : "";
  const isCoreRoute = currentPath === "/core" || (currentHost === "core.studox.eu" && (currentPath === "/" || currentPath === ""));
  if (isCoreRoute) {
    // Only render inline on core.studox.eu — all other domains redirect there
    if (currentHost !== "core.studox.eu") {
      import("@/utils/launchCore").then(({ launchCore }) => launchCore());
      return null;
    }
    return <StudoXCore />;
  }

  /* ── Auth bootstrap gates ───────────────────────────────────────────────────
     Children's data NEVER paints until the bootstrap reaches "ready" AND the store
     shows an authenticated, server-verified session. On a detected failure we show
     the error screen (→ status.studox.eu) instead of looping. ────────────────── */
  if (authPhase === "error") {
    return <AuthErrorScreen kind={authError} />;
  }
  if (authPhase !== "ready" || !isAuthenticated) {
    return <VerifyingScreen />;
  }

  /* ── backend.studox.eu — the secured Admin/Overseer console ─────────────────
     Extra-hardened on top of the normal auth gate above: the session must be
     server-verified (already true here) AND the role must be admin/solid_ground.
     Anyone else — logged in or not — is bounced straight back to Core, with no
     dashboard ever painting for them, not even for a frame. ─────────────────── */
  if (currentHost === "backend.studox.eu") {
    const role = user?.role;
    if (role !== "admin" && role !== "solid_ground") {
      window.location.replace(CORE_URL);
      return <VerifyingScreen />;
    }
    return <CoreDashboard onClose={() => { window.location.href = CORE_URL; }} />;
  }

  /* ── Launch animation overlay ──────────────────────────────────────────── */
  if (showLaunch) {
    return <LaunchAnimation user={user} onDone={handleLaunchDone} />;
  }

  const role = user?.role;

  return (
    <>
      <RewardOverlay />
      {isFirstLogin && <FirstLoginModal />}
      <AuthedRoutes role={role} />
      {/* NOVA ✦ — the AI buddy, docked bottom-right across Flow */}
      <NovaBuddy mode="dock" />
    </>
  );
}

/* ── Authenticated routes – split fullscreen vs AppShell ─────────────────── */
function AuthedRoutes({ role }) {
  const { pathname } = useLocation();
  const isFullscreen = pathname === "/minigame";
  const _host = typeof window !== "undefined" ? window.location.hostname : "";
  const isCoreRoute = pathname === "/core" || (_host === "core.studox.eu" && (pathname === "/" || pathname === ""));
  if (isCoreRoute) {
    if (_host !== "core.studox.eu") {
      import("@/utils/launchCore").then(({ launchCore }) => launchCore());
      return null;
    }
    return <StudoXCore />;
  }

  if (isFullscreen && role === "student") {
    return (
      <Routes>
        <Route path="/minigame" element={<MeteorStorm />}/>
        <Route path="*" element={<Navigate to="/minigame" replace/>}/>
      </Routes>
    );
  }

  return (
    <AppShell>
      <RouteChangeSound />
      <Routes>
          {/* ── Student ──────────────────────────────────────────────────── */}
          {role === "student" && (
            <>
              <Route path="/"              element={<Dashboard />} />
              <Route path="/lernen"        element={<LernWelten />} />
              <Route path="/quiz"          element={<Quiz />} />
              <Route path="/quiz/:id"      element={<Quiz />} />
              <Route path="/arena"         element={<Arena />} />
              <Route path="/leaderboard"   element={<Leaderboard />} />
              <Route path="/shop"          element={<Shop />} />
              <Route path="/vault"         element={<Vault />} />
              <Route path="/achievements"  element={<Achievements />} />
              <Route path="/messages"         element={<Messages />} />
              <Route path="/messages/:convId" element={<Messages />} />
              <Route path="/friends"          element={<Friends />} />
              <Route path="/profile"          element={<Profile />} />
              <Route path="/settings"      element={<Settings />} />
              <Route path="/tasks"         element={<Tasks />} />
              <Route path="/badges"        element={<Badges />} />
              <Route path="/titles"        element={<Titles />} />
              <Route path="/events"        element={<SeasonalEvents />} />
              <Route path="/analysis"     element={<Analysis />} />
              <Route path="/challenges"   element={<ClassChallenges />} />
              <Route path="/calendar"     element={<LernCalendar />} />
            </>
          )}

          {/* ── Teacher ──────────────────────────────────────────────────── */}
          {role === "teacher" && (
            <>
              <Route path="/"                 element={<TeacherDashboard />} />
              <Route path="/students"         element={<StudentList />} />
              <Route path="/students/:id"     element={<StudentDetail />} />
              <Route path="/quizzes"          element={<QuizBuilder />} />
              <Route path="/tasks"            element={<TaskManager />} />
              <Route path="/messages"         element={<Messages />} />
              <Route path="/messages/:convId" element={<Messages />} />
              <Route path="/leaderboard"      element={<Leaderboard />} />
              <Route path="/settings"         element={<Settings />} />
              <Route path="/challenges"       element={<ClassChallenges />} />
              <Route path="/profile"          element={<NonStudentProfile />} />
            </>
          )}

          {/* ── Admin → redirect to Core ─────────────────────────────── */}
          {role === "admin" && (
            <>
              <Route path="/messages"         element={<Messages />} />
              <Route path="/messages/:convId" element={<Messages />} />
              <Route path="/settings"         element={<Settings />} />
              <Route path="*"                 element={<AdminCoreRedirect />} />
            </>
          )}

          {/* ── Mentor ───────────────────────────────────────────────────── */}
          {role === "mentor" && (
            <>
              <Route path="/"                 element={<MentorDashboard />} />
              <Route path="/students"         element={<StudentList />} />
              <Route path="/students/:id"     element={<StudentDetail />} />
              <Route path="/codes"            element={<CodeManager />} />
              <Route path="/messages"         element={<Messages />} />
              <Route path="/messages/:convId" element={<Messages />} />
              <Route path="/leaderboard"      element={<Leaderboard />} />
              <Route path="/settings"         element={<Settings />} />
              <Route path="/profile"          element={<NonStudentProfile />} />
            </>
          )}

          {/* ── Parent ───────────────────────────────────────────────────── */}
          {role === "parent" && (
            <>
              <Route path="/"                 element={<ParentDashboard />} />
              <Route path="/messages"         element={<Messages />} />
              <Route path="/messages/:convId" element={<Messages />} />
              <Route path="/settings"         element={<Settings />} />
              <Route path="/profile"          element={<NonStudentProfile />} />
            </>
          )}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
  );
}
